// src/theme/designTokens.js
/**
 * KROMAIC Design System
 * Light luxury cosmetic brand design tokens
 */

export const designTokens = {
  // Color Palette
  colors: {
    // Core Brand Colors
    pearl: '#FAFAFA',
    nude: '#EDE6DA',
    sage: '#8FAF9A',
    champagne: '#C9A24D',
    charcoal: '#1A1A1A',

    // Extended Palette
    white: '#FFFFFF',
    black: '#000000',

    // Semantic Colors
    background: {
      primary: '#FAFAFA',
      secondary: '#EDE6DA',
      tertiary: '#F8F5F0',
      dark: '#1A1A1A',
      surface: '#FFFFFF',
      elevated: '#FFFFFF'
    },

    text: {
      primary: '#1A1A1A',
      secondary: '#666666',
      tertiary: '#999999',
      light: '#FFFFFF',
      muted: '#8FAF9A',
      disabled: '#CCCCCC'
    },

    border: {
      light: '#EDE6DA',
      medium: '#E0D6C5',
      strong: '#C9A24D',
      subtle: 'rgba(26, 26, 26, 0.1)',
      dark: 'rgba(0, 0, 0, 0.2)'
    },

    // Status Colors
    status: {
      success: '#8FAF9A',
      error: '#E57373',
      warning: '#FFB74D',
      info: '#64B5F6'
    },

    // Gradients
    gradients: {
      champagne: 'linear-gradient(135deg, #C9A24D 0%, #D4B56A 100%)',
      sage: 'linear-gradient(135deg, #8FAF9A 0%, #A3C0AB 100%)',
      luxury: 'linear-gradient(135deg, #FAFAFA 0%, #EDE6DA 100%)',
      dark: 'linear-gradient(135deg, #1A1A1A 0%, #333333 100%)',
      soft: 'linear-gradient(135deg, rgba(201, 162, 77, 0.1) 0%, rgba(143, 175, 154, 0.1) 100%)'
    },

    // Shadows
    shadows: {
      champagne: 'rgba(201, 162, 77, 0.3)',
      sage: 'rgba(143, 175, 154, 0.3)',
      charcoal: 'rgba(26, 26, 26, 0.1)'
    }
  },

  // Typography
  typography: {
    fontFamilies: {
      display: '"Playfair Display", "Georgia", serif',
      body: '"Poppins", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      mono: '"SF Mono", "Monaco", "Inconsolata", "Fira Code", monospace'
    },

    fontSizes: {
      xs: '0.75rem',    // 12px
      sm: '0.875rem',   // 14px
      base: '1rem',     // 16px
      lg: '1.125rem',   // 18px
      xl: '1.25rem',    // 20px
      '2xl': '1.5rem',  // 24px
      '3xl': '1.875rem',// 30px
      '4xl': '2.25rem', // 36px
      '5xl': '3rem',    // 48px
      '6xl': '3.75rem', // 60px
      '7xl': '4.5rem',  // 72px
      '8xl': '6rem',    // 96px
      '9xl': '8rem'     // 128px
    },

    fontWeights: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
      black: 900
    },

    lineHeights: {
      none: 1,
      tight: 1.25,
      snug: 1.375,
      normal: 1.5,
      relaxed: 1.625,
      loose: 2
    },

    letterSpacing: {
      tighter: '-0.05em',
      tight: '-0.025em',
      normal: '0',
      wide: '0.025em',
      wider: '0.05em',
      widest: '0.1em'
    }
  },

  // Spacing
  spacing: {
    px: '1px',
    0: '0',
    0.5: '0.125rem',
    1: '0.25rem',
    1.5: '0.375rem',
    2: '0.5rem',
    2.5: '0.625rem',
    3: '0.75rem',
    3.5: '0.875rem',
    4: '1rem',
    5: '1.25rem',
    6: '1.5rem',
    7: '1.75rem',
    8: '2rem',
    9: '2.25rem',
    10: '2.5rem',
    11: '2.75rem',
    12: '3rem',
    14: '3.5rem',
    16: '4rem',
    20: '5rem',
    24: '6rem',
    28: '7rem',
    32: '8rem',
    36: '9rem',
    40: '10rem',
    44: '11rem',
    48: '12rem',
    52: '13rem',
    56: '14rem',
    60: '15rem',
    64: '16rem',
    72: '18rem',
    80: '20rem',
    96: '24rem'
  },

  // Border Radius
  borderRadius: {
    none: '0',
    sm: '0.125rem',
    base: '0.25rem',
    md: '0.375rem',
    lg: '0.5rem',
    xl: '0.75rem',
    '2xl': '1rem',
    '3xl': '1.5rem',
    full: '9999px'
  },

  // Shadows
  shadows: {
    none: 'none',
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',

    // Luxury Shadows
    luxury: '0 20px 40px rgba(0, 0, 0, 0.05), 0 10px 20px rgba(0, 0, 0, 0.03), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
    'luxury-lg': '0 40px 80px rgba(0, 0, 0, 0.08), 0 20px 40px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.15)',
    'luxury-xl': '0 60px 120px rgba(0, 0, 0, 0.1), 0 30px 60px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
    glow: '0 0 40px rgba(201, 162, 77, 0.15)',
    'glow-lg': '0 0 80px rgba(201, 162, 77, 0.25)'
  },

  // Animations
  animations: {
    durations: {
      fast: '150ms',
      base: '300ms',
      slow: '500ms',
      luxury: '800ms',
      epic: '1200ms'
    },

    easings: {
      linear: 'linear',
      ease: 'ease',
      'ease-in': 'ease-in',
      'ease-out': 'ease-out',
      'ease-in-out': 'ease-in-out',
      luxury: 'cubic-bezier(0.43, 0.13, 0.23, 0.96)',
      bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)'
    },

    delays: {
      none: '0ms',
      short: '100ms',
      base: '200ms',
      long: '500ms'
    }
  },

  // Z-Index Scale
  zIndex: {
    hide: -1,
    auto: 'auto',
    base: 0,
    docked: 10,
    dropdown: 1000,
    sticky: 1100,
    banner: 1200,
    overlay: 1300,
    modal: 1400,
    popover: 1500,
    skipLink: 1600,
    toast: 1700,
    tooltip: 1800
  },

  // Breakpoints
  breakpoints: {
    xs: '320px',
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px'
  }
};

// CSS Variables for global styles
export const cssVariables = {
  // Color Variables
  '--color-pearl': designTokens.colors.pearl,
  '--color-nude': designTokens.colors.nude,
  '--color-sage': designTokens.colors.sage,
  '--color-champagne': designTokens.colors.champagne,
  '--color-charcoal': designTokens.colors.charcoal,

  // Text Colors
  '--color-text-primary': designTokens.colors.text.primary,
  '--color-text-secondary': designTokens.colors.text.secondary,
  '--color-text-tertiary': designTokens.colors.text.tertiary,

  // Background Colors
  '--color-background-primary': designTokens.colors.background.primary,
  '--color-background-secondary': designTokens.colors.background.secondary,
  '--color-background-dark': designTokens.colors.background.dark,

  // Font Families
  '--font-display': designTokens.typography.fontFamilies.display,
  '--font-body': designTokens.typography.fontFamilies.body,

  // Animation Easings
  '--ease-luxury': designTokens.animations.easings.luxury,
  '--ease-bounce': designTokens.animations.easings.bounce,

  // Animation Durations
  '--duration-fast': designTokens.animations.durations.fast,
  '--duration-base': designTokens.animations.durations.base,
  '--duration-slow': designTokens.animations.durations.slow,
  '--duration-luxury': designTokens.animations.durations.luxury
};

// Helper function to apply CSS variables
export const applyCSSVariables = () => {
  const root = document.documentElement;
  Object.entries(cssVariables).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });
};

// Export theme object for styled-components or emotion
export const theme = {
  ...designTokens,
  applyCSSVariables
};

export default theme;